import {
  Component,
  Input,
  ViewChild,
  forwardRef,
  ChangeDetectionStrategy,
  signal,
  inject,
} from "@angular/core";
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule,
} from "@angular/forms";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import {
  NgbTypeaheadModule,
  NgbTypeahead,
  NgbHighlight,
} from "@ng-bootstrap/ng-bootstrap";
import { Observable, of } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  catchError,
  map,
} from "rxjs/operators";
import { Company } from "../../../api/models/company";
import { CompanyService } from "../../services/company.service";

const noop = () => {};

export const COMPANY_BLACKLIST_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => CompanyBlacklistInputComponent),
  multi: true,
};

@Component({
  selector: "app-company-blacklist-input",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    FontAwesomeModule,
    NgbTypeaheadModule,
    NgbHighlight,
  ],
  providers: [COMPANY_BLACKLIST_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="company-blacklist-container">
      <div class="input-group">
        <input
          type="text"
          class="form-control"
          [ngbTypeahead]="searchCompanies"
          [inputFormatter]="formatter"
          [resultTemplate]="companyResultTemplate"
          [editable]="false"
          [focusFirst]="false"
          (selectItem)="onSelectItem($event)"
          [(ngModel)]="inputModel"
          [placeholder]="placeholder | translate"
        />
      </div>

      @if (selectedCompanies().length > 0) {
        <div class="mt-2">
          @for (company of selectedCompanies(); track company.id) {
            <span
              class="badge badge-primary mr-2 mb-2 p-2 d-inline-flex align-items-center"
            >
              {{ company.name }}
              <button
                class="btn btn-sm btn-link text-white p-0 ml-1"
                (click)="removeCompany(company)"
                type="button"
              >
                <fa-icon [icon]="faTimes" class="small"></fa-icon>
              </button>
            </span>
          }
        </div>
      }
    </div>

    <ng-template #companyResultTemplate let-r="result" let-t="term">
      <ngb-highlight [result]="r.name ?? ''" [term]="t"></ngb-highlight>
    </ng-template>
  `,
  styles: [
    `
      .company-blacklist-container .badge {
        font-weight: 500;
        font-size: 0.875rem;
      }
    `,
  ],
})
export class CompanyBlacklistInputComponent implements ControlValueAccessor {
  @Input() placeholder = "Search for a company";

  @ViewChild(NgbTypeahead) typeaheadInstance?: NgbTypeahead;

  readonly faTimes = faTimes;

  selectedCompanies = signal<Company[]>([]);
  inputModel: any = "";

  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (value: Company[]) => void = noop;

  private readonly companyService = inject(CompanyService);

  searchCompanies = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      switchMap((term) => {
        if (!term || term.length < 2) return of<Company[]>([]);
        return (
          this.companyService.apiGetCompanies({
            SearchTerm: term,
            PageSize: 10,
          }) as Observable<any>
        ).pipe(
          map((response) => {
            const rows: Company[] =
              response?.data ?? response?.items ?? response ?? [];
            const selectedIds = new Set(
              this.selectedCompanies()
                .map((c) => c.id)
                .filter(Boolean)
            );
            return rows
              .filter((c) => c?.id && !selectedIds.has(c.id))
              .slice(0, 10);
          }),
          catchError(() => of<Company[]>([]))
        );
      })
    );

  formatter = (company: Company | string) =>
    typeof company === "string" ? company : company?.name ?? "";

  onSelectItem(event: any): void {
    event.preventDefault();
    const company: Company | undefined = event.item;
    if (!company?.id) return;
    this.addCompany(company);
    this.inputModel = "";
  }

  removeCompany(company: Company): void {
    const next = this.selectedCompanies().filter((c) => c.id !== company.id);
    this.selectedCompanies.set(next);
    this.onChangeCallback(next);
    this.onTouchedCallback();
  }

  private addCompany(company: Company): void {
    const current = this.selectedCompanies();
    if (current.some((c) => c.id === company.id)) return;
    const next = [...current, company];
    this.selectedCompanies.set(next);
    this.onChangeCallback(next);
    this.onTouchedCallback();
  }

  writeValue(value: Company[] | null): void {
    this.selectedCompanies.set(Array.isArray(value) ? value : []);
  }

  registerOnChange(fn: (value: Company[]) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }
}
